import { useEffect, useState } from 'react';
import { Theme, useMediaQuery } from '@mui/material';
import { CoreBox, CoreFormControlLabel, CoreRadio, CoreTextField, CoreDialog, CoreTypography } from '@youscience/core';

import { SurveyQuestionsData } from '@interfaces/favoriteSurvey';

import useMajorSurveyStore from '@store/MajorSurveyStore';

import { FAVORITE_MAJORS_QUESTION_ID, SCORE_DATA, SURVEYS_ID } from '@constants/surveys';
import { majorsDescriptionMap } from '@common-types';

import DialogFooter from '@components/DialogFooter';

import {
  sxStyles,
  StyledDialogTitle,
  StyledDialogContent,
  StyledScoreRadioButton,
  StyledRadioGroup,
  StyledScoreButtonsWrap,
  StyledRadio,
} from './MajorSurvey.styles';

interface MajorSurveyProps {
  programCipCode: string;
  isOpenSurvey: boolean;
  onCloseSurvey: () => void;
  onOpenSuccessDialog: () => void;
}

export const MajorSurvey = (props: MajorSurveyProps) => {
  const { programCipCode, isOpenSurvey, onCloseSurvey, onOpenSuccessDialog } = props;

  const isLoading = useMajorSurveyStore((state) => state.isLoading);

  const { setFavoriteMajorSurvey, getFavoriteMajorSurvey } = useMajorSurveyStore((state) => state);

  const isDesktopScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));

  const initialSurveyData = {} as SurveyQuestionsData;
  const [temporarySurveyData, setTemporarySurveyData] = useState(initialSurveyData);

  const programName = majorsDescriptionMap.get(programCipCode)?.cipTitle;

  const handleRadioChange = (questionId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setTemporarySurveyData((prevState) => ({
      ...prevState,
      [questionId]: event.target.value,
    }));
  };

  const handleOpenQuestionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.trim().length > 2000) return;

    setTemporarySurveyData((prevState) => ({
      ...prevState,
      [FAVORITE_MAJORS_QUESTION_ID.THIRD]: event.target.value,
    }));
  };

  const handleSkipSurveyData = () => {
    onCloseSurvey();
    setTemporarySurveyData(initialSurveyData);
  };

  const handleSaveSurveyData = () => {
    onCloseSurvey();

    if (JSON.stringify(initialSurveyData) !== JSON.stringify(temporarySurveyData)) {
      onOpenSuccessDialog();

      const preparedQuestionsData = Object.entries(temporarySurveyData).map(([key, value]) => ({
        questionId: +key,
        answer: value,
      }));

      const postData = {
        majorId: programCipCode,
        surveyId: SURVEYS_ID.FAVORITE_MAJORS,
        questions: preparedQuestionsData,
      };

      setFavoriteMajorSurvey(postData);
    }
  };

  useEffect(() => {
    const fetchSurveyData = async () => {
      const surveyQuestionData = await getFavoriteMajorSurvey(programCipCode);

      setTemporarySurveyData(surveyQuestionData);
    };

    if (!Object.keys(initialSurveyData).length) {
      void fetchSurveyData();
    }
  }, []);

  return (
    <CoreDialog
      sx={sxStyles.dialogWrap}
      open={isOpenSurvey}
      onClose={handleSkipSurveyData}
      aria-labelledby="survey-title"
    >
      <StyledDialogTitle id="survey-title">{`What do you like about ${programName as string}?`}</StyledDialogTitle>

      <StyledDialogContent data-testid="surveyContent">
        <CoreTypography sx={sxStyles.subtitle}>
          Help us understand why you like this major/program by answering the questions below.
        </CoreTypography>

        <CoreBox sx={sxStyles.questionWrap} data-testid="firstQuestion">
          <CoreTypography sx={sxStyles.question} variant="h5" component="h4">
            This major/program would prepare me well for my future career.
          </CoreTypography>

          <StyledRadioGroup onChange={handleRadioChange(FAVORITE_MAJORS_QUESTION_ID.FIRST)}>
            <StyledScoreButtonsWrap>
              {SCORE_DATA.map(({ value, label }) => {
                const isSelected = value === temporarySurveyData[FAVORITE_MAJORS_QUESTION_ID.FIRST];

                return (
                  <CoreBox key={value}>
                    <StyledScoreRadioButton
                      isSelected={isSelected}
                      disabled={isLoading}
                      data-testid={`firstQuestionOption${value}`}
                    >
                      <StyledRadio value={value} />

                      <CoreTypography>{value}</CoreTypography>
                    </StyledScoreRadioButton>

                    {(isDesktopScreen || +value % 2 !== 0) && (
                      <CoreTypography sx={sxStyles.scoreLabel}>{label}</CoreTypography>
                    )}
                  </CoreBox>
                );
              })}
            </StyledScoreButtonsWrap>

            <CoreFormControlLabel
              control={
                <CoreRadio
                  checked={temporarySurveyData[FAVORITE_MAJORS_QUESTION_ID.FIRST] === '0'}
                  value="0"
                  disabled={isLoading}
                />
              }
              label="Not sure"
              data-testid="surveyQuestion1AnswerNotSure"
            />

            {temporarySurveyData[FAVORITE_MAJORS_QUESTION_ID.FIRST] === '0' && (
              <CoreTypography data-testid="surveyQuestion1HelperText">
                Check out the careers page to explore careers that could be a good fit for you.
              </CoreTypography>
            )}
          </StyledRadioGroup>
        </CoreBox>

        <CoreBox sx={sxStyles.questionWrap} data-testid="secondQuestion">
          <CoreTypography sx={sxStyles.question} variant="h5" component="h4">
            Have you explored internships, apprenticeships, or WBL opportunities that align with this program/career?
          </CoreTypography>

          <StyledRadioGroup row onChange={handleRadioChange(FAVORITE_MAJORS_QUESTION_ID.SECOND)}>
            <CoreFormControlLabel
              sx={sxStyles.coreRadioButton}
              control={
                <CoreRadio
                  checked={temporarySurveyData[FAVORITE_MAJORS_QUESTION_ID.SECOND] === '1'}
                  value="1"
                  disabled={isLoading}
                />
              }
              label="Yes"
            />

            <CoreFormControlLabel
              sx={sxStyles.coreRadioButton}
              control={
                <CoreRadio
                  checked={temporarySurveyData[FAVORITE_MAJORS_QUESTION_ID.SECOND] === '2'}
                  value="2"
                  disabled={isLoading}
                />
              }
              label="No"
            />

            <CoreFormControlLabel
              control={
                <CoreRadio
                  checked={temporarySurveyData[FAVORITE_MAJORS_QUESTION_ID.SECOND] === '0'}
                  value="0"
                  disabled={isLoading}
                />
              }
              label="Not sure"
            />

            {temporarySurveyData[FAVORITE_MAJORS_QUESTION_ID.SECOND] === '0' && (
              <CoreTypography>
                Ask your counselor or teacher about opportunities or research that can help you determine if this major
                is a good fit for you.
              </CoreTypography>
            )}
          </StyledRadioGroup>
        </CoreBox>

        <CoreBox data-testid="notesQuestion">
          <CoreTypography sx={sxStyles.notesTitle} variant="h5" component="h4">
            Do you have any other thoughts or things you would need to research further about this program or major?
          </CoreTypography>
          {!isLoading && (
            <CoreTextField
              sx={sxStyles.notesField}
              placeholder="Please write your notes here"
              multiline
              fullWidth
              rows={2}
              value={temporarySurveyData[FAVORITE_MAJORS_QUESTION_ID.THIRD]}
              onChange={handleOpenQuestionChange}
              disabled={isLoading}
            />
          )}
        </CoreBox>
      </StyledDialogContent>

      <DialogFooter
        goBackButtonTitle="Skip the survey"
        goNextButtonTitle="Save"
        goBackActionButtonTitle="Saving"
        onGoBack={handleSkipSurveyData}
        onGoNext={handleSaveSurveyData}
      />
    </CoreDialog>
  );
};
